import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { XmarkStroke } from '@lineiconshq/free-icons';
import { Maximize2, Minimize2 } from 'lucide-react';
import { useMemo } from 'react';
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '@/components/ui/dialog';
import type { SearchFieldColumn, SearchFieldDialogAction, SearchRequestFn } from '../types';
import { SearchResultsTable } from './search-results-table';
import { useDialogStack } from '@/hooks/use-dialog-stack';
import { AritoIcon } from '@/components/custom/arito';
import { normalizeSearchColumns } from '../helpers';
import { cn } from '@/lib/utils';

interface SearchDialogProps<T> {
  checkboxSelection?: boolean;
  dialogBaseZIndex?: number;
  dialogPageSize?: number;
  dialogActions?: SearchFieldDialogAction<T>[];
  dialogTitle: string;
  headerFields?: React.ReactNode;
  hideRowNumberColumn?: boolean;
  isConfirmDisabled?: boolean;
  isFullScreen?: boolean;
  onCancel: () => void;
  onCloseDialog?: () => void;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  onRowDoubleClick?: (row: T) => void;
  onRowSelection?: (row: T | null) => void;
  onSelectionRowsChange?: (rows: T[]) => void;
  onToggleFullScreen?: () => void;
  open: boolean;
  refreshKey?: string | number;
  rows?: T[];
  searchColumns?: SearchFieldColumn<T>[];
  searchRequest: SearchRequestFn<T> | null;
  selectedRows?: T[];
  selectedRowId?: string | null;
}

const headerBarClassName =
  'flex h-[var(--erp-dialog-header-height)] min-h-[var(--erp-dialog-header-height)] items-center justify-between border-b border-[#d1d5db] bg-[#f2f7fc] pl-3.5 pr-4';

const footerBarClassName =
  'flex h-[var(--erp-dialog-footer-height)] min-h-[var(--erp-dialog-footer-height)] items-center justify-end gap-3 border-t border-[#e5e7eb] bg-[#f2f7fc] px-4';

const actionButtonClassName =
  'inline-flex h-[var(--erp-command-button-height)] min-w-[100px] items-center justify-center rounded-[3px] px-4 text-[15px] font-semibold transition-colors';
const viewportClassName = 'fixed inset-0 flex items-center justify-center p-4';

const INDEX_COLUMN_WIDTH = 50;
const DIALOG_CHROME_WIDTH = 48;
const MIN_DIALOG_WIDTH = 560;
const MAX_DIALOG_WIDTH = 1280;

const getDialogColumnWidth = (column: { width?: number; minWidth?: number; flex?: number }) => {
  if (typeof column.width === 'number') return column.width;
  if (typeof column.minWidth === 'number') return column.minWidth;
  if (typeof column.flex === 'number') return Math.max(120, Math.round(column.flex * 140));
  return 120;
};

export const SearchDialog = <T,>({
  checkboxSelection = false,
  dialogBaseZIndex,
  dialogPageSize,
  dialogTitle,
  dialogActions,
  headerFields,
  hideRowNumberColumn,
  isConfirmDisabled,
  isFullScreen,
  onCancel,
  onCloseDialog,
  onConfirm,
  onOpenChange,
  onRowDoubleClick,
  onRowSelection,
  onSelectionRowsChange,
  onToggleFullScreen,
  open,
  refreshKey,
  rows,
  searchColumns,
  searchRequest,
  selectedRows,
  selectedRowId
}: SearchDialogProps<T>) => {
  const { contentZIndex, overlayZIndex } = useDialogStack(
    open,
    dialogBaseZIndex === undefined ? undefined : { baseZIndex: dialogBaseZIndex }
  );
  const dialogWidth = useMemo(() => {
    const normalizedColumns = normalizeSearchColumns(searchColumns);
    const tableWidth =
      normalizedColumns.reduce((total, column) => total + getDialogColumnWidth(column), 0) +
      (hideRowNumberColumn ? 0 : INDEX_COLUMN_WIDTH);

    const contentWidth = Math.max(MIN_DIALOG_WIDTH, tableWidth + DIALOG_CHROME_WIDTH);

    return Math.min(contentWidth, MAX_DIALOG_WIDTH);
  }, [hideRowNumberColumn, searchColumns]);

  return (
    <Dialog modal={true} open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className='bg-black/50 backdrop-blur-none' style={{ zIndex: overlayZIndex }} />
        <div
          className={cn(viewportClassName, isFullScreen && 'items-stretch justify-stretch p-0')}
          style={{ zIndex: contentZIndex }}
        >
          <DialogPrimitive.Content
            onOpenAutoFocus={event => {
              event.preventDefault();
            }}
            onCloseAutoFocus={event => {
              event.preventDefault();
            }}
            onInteractOutside={event => {
              event.preventDefault();
            }}
            onPointerDownOutside={event => {
              event.preventDefault();
            }}
            onFocusOutside={event => {
              event.preventDefault();
            }}
            className={cn(
              'flex max-h-[80vh] flex-col overflow-hidden border border-[#cfd8e3] bg-white shadow-lg outline-none',
              isFullScreen && 'h-full max-h-none w-full max-w-none overflow-hidden border-0'
            )}
            style={
              isFullScreen
                ? { height: '100%', width: '100%' }
                : {
                    height: 'min(90vh, calc(100vh - 32px))',
                    width: `min(calc(100vw - 32px), ${dialogWidth}px)`,
                    maxWidth: 'calc(100vw - 32px)'
                  }
            }
          >
            <div className={headerBarClassName}>
              <DialogTitle className='flex min-w-0 flex-1 items-center text-[length:var(--erp-dialog-title-size)] font-bold text-black'>
                <AritoIcon icon={584} className='mx-2 flex-none' />
                <span className='truncate'>{dialogTitle}</span>
              </DialogTitle>

              <div className='flex items-center gap-2'>
                {onToggleFullScreen && (
                  <button
                    type='button'
                    onClick={onToggleFullScreen}
                    className='inline-flex size-8 items-center justify-center text-black/80 hover:text-black'
                    title={isFullScreen ? 'Thu nhỏ' : 'Phóng to'}
                  >
                    {isFullScreen ? <Minimize2 className='size-[18px]' /> : <Maximize2 className='size-[18px]' />}
                  </button>
                )}
                <button
                  type='button'
                  onClick={onCancel}
                  className='inline-flex size-8 items-center justify-center text-black/80 hover:text-black'
                  title='Đóng'
                >
                  <Lineicons aria-hidden='true' icon={XmarkStroke} className='size-[18px]' />
                </button>
              </div>
            </div>

            <div className='flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'>
              <SearchResultsTable
                checkboxSelection={checkboxSelection}
                pageSize={dialogPageSize}
                dialogActions={dialogActions}
                fillAvailableHeight
                open={open}
                refreshKey={refreshKey}
                rows={rows}
                searchColumns={searchColumns}
                searchRequest={searchRequest}
                selectedRowId={selectedRowId}
                selectedRows={selectedRows}
                onRowSelection={onRowSelection}
                onSelectionRowsChange={onSelectionRowsChange}
                onRowDoubleClick={onRowDoubleClick}
                hideRowNumberColumn={hideRowNumberColumn}
                headerFields={headerFields}
                onCloseDialog={onCloseDialog}
              />
            </div>

            <div className={footerBarClassName}>
              <button
                type='button'
                onClick={onConfirm}
                disabled={isConfirmDisabled}
                className={cn(
                  actionButtonClassName,
                  'bg-main text-main-foreground hover:bg-main/80 disabled:bg-gray-400'
                )}
              >
                <AritoIcon icon={884} className='mr-2' />
                <span>Đồng ý</span>
              </button>
              <button
                type='button'
                onClick={onCancel}
                className={cn(actionButtonClassName, 'border border-main bg-white text-main hover:bg-main/10')}
              >
                <AritoIcon icon={885} className='mr-2' />
                <span>Hủy</span>
              </button>
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
    </Dialog>
  );
};
